import type { SaveSelfAssessmentResultsInput } from "@/graphql/generated/types";

// Helpers for the per-element self-assessment form.
//
// The UI uses a 5-point Likert scale. The backend still stores a 0-100
// `scorePercent` (SAM_RESULT.score), so each Likert level maps to a canonical
// percent here - this map is the SINGLE source of truth: switching to a native
// Likert field later only needs the migration to reuse `likertToPercent` /
// `percentToLikert`. `AnswerMap` holds the chosen Likert LEVEL (1..5).

export type AnswerMap = Record<string, number | undefined>;

export interface LikertLevel {
  level: number; // 1..5
  labelId: string; // i18n message id (resolve via intl) - see `likert.*`
  percent: number; // canonical 0-100 stored in SAM_RESULT.score
}

// 5 levels spread evenly across 0-100 (step 100/(5-1) = 25). Reversible.
export const LIKERT_LEVELS: readonly LikertLevel[] = [
  { level: 1, labelId: "likert.1", percent: 0 },
  { level: 2, labelId: "likert.2", percent: 25 },
  { level: 3, labelId: "likert.3", percent: 50 },
  { level: 4, labelId: "likert.4", percent: 75 },
  { level: 5, labelId: "likert.5", percent: 100 },
];

export function likertToPercent(level: number): number {
  return LIKERT_LEVELS.find((l) => l.level === level)?.percent ?? 50;
}

// Nearest Likert level for an existing 0-100 score (legacy/continuous data).
export function percentToLikert(percent: number): number {
  const clamped = Math.max(0, Math.min(100, percent));
  return Math.round(clamped / 25) + 1; // 0->1, 25->2, 50->3, 75->4, 100->5
}

export function buildSaveResultsInput(
  answers: AnswerMap,
  knownQuestionIds: readonly string[],
): SaveSelfAssessmentResultsInput {
  const results: { questionId: string; scorePercent: number }[] = [];
  for (const questionId of knownQuestionIds) {
    const level = answers[questionId];
    if (level === undefined) continue;
    // AnswerMap stores the Likert level; persist the mapped percent.
    results.push({ questionId, scorePercent: likertToPercent(level) });
  }
  return { results };
}

export function isAssessmentComplete(
  answers: AnswerMap,
  knownQuestionIds: readonly string[],
): boolean {
  return knownQuestionIds.every((id) => answers[id] !== undefined);
}

// --- How far through the questionnaire they are -----------------------------
//
// The score is NOT guarded by completeness anywhere below it. The genesis trigger
// (V0011__ElementScoreLog.sql) is `round(avg(...))` over the `sam_result` rows that
// exist, with no condition on how many there are, so one statement rated 95 yields a
// score of 95 and the band `stark`. On screen that is indistinguishable from a score
// earned over twenty answers, and nothing in the payload marks it as provisional.
//
// So the ratio is the only signal, and it decides whether the assistant may evaluate
// the score at all.

export const QUESTIONNAIRE_COMPLETE_RATIO = 0.75;

export type QuestionnaireState = "complete" | "partial" | "unstarted";

/**
 * `complete` at 75% answered or more: the score carries enough items to be evaluated.
 * `unstarted` means literally nothing answered. Everything between is `partial`.
 *
 * The line for `unstarted` is a COUNT, not a ratio, and deliberately so: with a ratio the
 * assistant told someone who had answered 5 of 24 that they had "den Fragebogen noch nicht
 * begonnen (0 von 24)", because 5/24 fell under the old 25% line and the model read the word
 * `unstarted` as the number zero. Someone who has started gets asked to continue, never to begin.
 */
export function questionnaireState(answered: number, total: number): QuestionnaireState {
  if (!Number.isFinite(total) || total <= 0) return "unstarted";
  const done = Math.max(0, answered);
  if (done === 0) return "unstarted";
  return done / total >= QUESTIONNAIRE_COMPLETE_RATIO ? "complete" : "partial";
}

// --- Reading saved answers back ---------------------------------------------
//
// Two functions, deliberately not one. `joinSavedAnswers` is the RAW join the
// assessment form needs: it must never invert, or re-saving would write the
// flipped value back to SAM_RESULT.score via `buildSaveResultsInput`.
// `weakestStatements` is the only place inversion is applied, and it is read-only.

export interface SavedAnswer {
  questionId: string;
  scorePercent: number;
}

export interface StatementLike {
  id: string;
  invertResult: boolean;
  content?: { localized?: string | null } | null;
}

/** Saved answers as Likert levels, keyed by question id. No inversion. */
export function joinSavedAnswers(
  questionIds: readonly string[],
  savedAnswers: readonly SavedAnswer[] | null | undefined,
): AnswerMap {
  const known = new Set(questionIds);
  const out: AnswerMap = {};
  for (const row of savedAnswers ?? []) {
    if (!known.has(row.questionId)) continue;
    out[row.questionId] = percentToLikert(row.scorePercent);
  }
  return out;
}

export interface WeakStatement {
  id: string;
  text: string;
  /** 0-100, POWER-ALIGNED: low always means "little of this power". */
  effective: number;
  inverted: boolean;
}

export const WEAK_THRESHOLD = 60;
const WEAK_MAX = 5;
const WEAK_MIN = 3;

/**
 * The statements a person rated weakest for one power, weakest first.
 *
 * `scorePercent` is the RAW agreement the person expressed. Inversion lives in
 * exactly one place in the product - the Postgres trigger that averages into
 * `element_score_log` (`genesis V0011__ElementScoreLog.sql:39-41`) - so a high raw
 * value on a negatively worded statement is a WEAK showing of the power. Ranking on
 * the raw value would return the statements the person answered *well*.
 *
 * Unanswered is unknown, never 0: `selfAssessmentSavedAnswers` returns only answered
 * rows, and a partially answered questionnaire is the normal case.
 */
export function weakestStatements(
  statements: readonly StatementLike[],
  savedAnswers: readonly SavedAnswer[] | null | undefined,
  options: { threshold?: number; min?: number; max?: number } = {},
): WeakStatement[] {
  const threshold = options.threshold ?? WEAK_THRESHOLD;
  const min = options.min ?? WEAK_MIN;
  const max = options.max ?? WEAK_MAX;

  const byId = new Map((savedAnswers ?? []).map((a) => [a.questionId, a.scorePercent]));
  const answered: WeakStatement[] = [];
  statements.forEach((s) => {
    const raw = byId.get(s.id);
    if (raw === undefined) return;
    // sam_result.score is int2 with no CHECK constraint; clamp before inverting.
    const clamped = Math.max(0, Math.min(100, raw));
    answered.push({
      id: s.id,
      text: s.content?.localized ?? "",
      effective: s.invertResult ? 100 - clamped : clamped,
      inverted: s.invertResult,
    });
  });
  if (answered.length === 0) return [];

  // Stable: equal values keep server order, which is `sam_question_id` order.
  const sorted = answered
    .map((s, i) => ({ s, i }))
    .sort((a, bb) => a.s.effective - bb.s.effective || a.i - bb.i)
    .map(({ s }) => s);

  const below = sorted.filter((s) => s.effective < threshold);
  // Too few genuinely weak ones: fall back to the relatively weakest, so a high
  // scorer still gets something specific rather than a generic answer.
  return below.length >= min ? below.slice(0, max) : sorted.slice(0, Math.min(min, sorted.length));
}
